import { useCallback, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Select, F0Text } from '@factorialco/f0-react'
import { Input, Textarea } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { ApproverSelectionContainer } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/ApproverSelectionContainer'
import { BasicTaskHandlerSchema } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/basicTaskHandler'
import { StrategyItem } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/utils/interfaces'
import {
  strategiesFromPayload,
  strategiesToPayload,
} from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/utils/strategyUtils'
import { type HandlerFormComponent } from 'modules/workflows/contexts/WorkflowContext'
import { getDurationUnitOptions } from 'modules/workflows/lib/durationUnit'

export const BasicTaskHandlerForm = ({ readonly }: HandlerFormComponent) => {
  const { control, watch, setValue, getValues } = useFormContext<BasicTaskHandlerSchema>()

  const payload = watch('nodeData.payload')

  const { strategies, fallbackStrategies } = useMemo(() => {
    if (!payload) {
      return {
        strategies: [],
        fallbackStrategies: [],
      }
    }
    return strategiesFromPayload<'strategies' | 'fallbackStrategies'>([
      {
        type: 'ids',
        data: payload.employee_ids,
        groupAs: 'strategies',
      },
      {
        type: 'ids',
        data: payload.fallback_employee_ids,
        groupAs: 'fallbackStrategies',
      },
      {
        type: 'segments',
        data: payload.employee_segments,
        groupAs: 'strategies',
      },
      {
        type: 'segments',
        data: payload.fallback_employee_segments,
        groupAs: 'fallbackStrategies',
      },
    ])
  }, [payload])

  const handleMutateStrategies = useCallback(
    (data: { add?: StrategyItem[]; fallbackAdd?: StrategyItem[]; remove?: string }) => {
      if (!data.add?.length && !data.fallbackAdd?.length && !data.remove) {
        return
      }

      const { add = [], fallbackAdd = [], remove } = data

      const allStrategies = [...strategies, ...add].filter((strategy) => remove !== strategy.id)
      const allFallbackStrategies = [...fallbackStrategies, ...fallbackAdd].filter(
        (strategy) => remove !== strategy.id
      )

      const currentPayload = getValues().nodeData.payload

      const newPayload = strategiesToPayload<typeof payload>(
        [
          {
            field: 'employee_ids',
            type: 'ids',
            data: allStrategies,
          },
          {
            field: 'employee_segments',
            type: 'segments',
            data: allStrategies,
          },
          {
            field: 'fallback_employee_ids',
            type: 'ids',
            data: allFallbackStrategies,
          },
          {
            field: 'fallback_employee_segments',
            type: 'segments',
            data: allFallbackStrategies,
          },
        ],
        currentPayload
      )

      setValue('nodeData.payload', newPayload, { shouldValidate: true, shouldDirty: true })
    },
    [strategies, fallbackStrategies, getValues, setValue]
  )

  const unselectableEmployees = useMemo(
    () => [payload?.employee_ids, payload?.fallback_employee_ids].flat().filter(Boolean),
    [payload?.employee_ids, payload?.fallback_employee_ids]
  )

  const unselectableTeams = useMemo(
    () =>
      [payload?.employee_segments, payload?.fallback_employee_segments]
        .flat()
        .filter((segment) => segment && ['anyone_in_team', 'team_leads'].includes(segment.name))
        .map(
          (segment) => segment && ('team_id' in segment.input ? segment.input.team_id : undefined)
        )
        .filter(Boolean),
    [payload?.employee_segments, payload?.fallback_employee_segments]
  )

  const unselectableRoles = useMemo(
    () =>
      [payload?.employee_segments, payload?.fallback_employee_segments]
        .flat()
        .filter((segment) => segment && segment.name === 'job_catalog_role')
        .map((segment) =>
          segment && 'role_id' in segment.input ? segment.input.role_id : undefined
        )
        .filter(Boolean),
    [payload?.employee_segments, payload?.fallback_employee_segments]
  )

  const unselectableStrategies = useMemo(
    () =>
      [strategies, fallbackStrategies]
        .flat()
        .filter((strategy) =>
          ['employee_manager', 'admins', 'resource_owner'].includes(strategy.type)
        )
        .map((strategy) => strategy.type),
    [strategies, fallbackStrategies]
  )

  return (
    <Box gap="s16">
      {/* Task Title */}
      <Box gap="s8" width="full">
        <F0Text content={i18n.t('workflows.sidepanel.task.title_label')} required />
        <Controller
          name="nodeData.payload.title"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              size="md"
              label={i18n.t('workflows.sidepanel.task.title_label')}
              hideLabel
              placeholder={i18n.t('workflows.sidepanel.task.title_placeholder')}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
              required
              disabled={readonly}
            />
          )}
        />
      </Box>

      {/* Task Description */}
      <Box gap="s8" width="full">
        <Controller
          name="nodeData.payload.description"
          control={control}
          render={({ field, fieldState }) => (
            <Textarea
              label={i18n.t('workflows.sidepanel.task.description_label')}
              placeholder={i18n.t('workflows.sidepanel.task.description_placeholder')}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
              required
              disabled={readonly}
            />
          )}
        />
      </Box>

      {/* Assignees */}
      <Box gap="s8" width="full">
        <F0Text content={i18n.t('workflows.sidepanel.task.assignees_label')} required />
        <ApproverSelectionContainer
          entityLabel={i18n.t('workflows.sidepanel.approval.entities.assignee')}
          newEntityLabel={i18n.t('workflows.sidepanel.approval.fields.new_assignee')}
          placeholder={i18n.t('workflows.sidepanel.approval.fields.new_assignee_placeholder')}
          onAddStrategies={(data) => handleMutateStrategies({ add: data })}
          onDeleteStrategy={(id) => handleMutateStrategies({ remove: id })}
          strategies={strategies}
          unselectableEmployees={unselectableEmployees}
          unselectableRoles={unselectableRoles}
          unselectableStrategies={unselectableStrategies}
          unselectableTeams={unselectableTeams}
          readonly={readonly}
        />
      </Box>
      {
        /* Fallback Assignees */
        <Box gap="s8" width="full">
          <F0Text content={i18n.t('workflows.sidepanel.approval.fields.fallback_assignee')} />
          <ApproverSelectionContainer
            entityLabel={i18n.t('workflows.sidepanel.approval.entities.fallback_assignee')}
            newEntityLabel={i18n.t('workflows.sidepanel.approval.fields.new_fallback_assignee')}
            placeholder={i18n.t(
              'workflows.sidepanel.approval.fields.new_fallback_assignee_placeholder'
            )}
            maxStrategies={1}
            onAddStrategies={(data) => handleMutateStrategies({ fallbackAdd: data })}
            onDeleteStrategy={(id) => handleMutateStrategies({ remove: id })}
            strategies={fallbackStrategies}
            unselectableEmployees={unselectableEmployees}
            unselectableRoles={unselectableRoles}
            unselectableStrategies={unselectableStrategies}
            unselectableTeams={unselectableTeams}
            readonly={readonly}
          />
        </Box>
      }

      {/* Due On */}
      <Box gap="s8" width="full">
        <F0Text content={i18n.t('workflows.sidepanel.task.due_on_label')} required />
        <Box flexDirection="row" gap="s8" width="full" alignItems="baseline">
          <Box style={{ width: '100px' }}>
            <Controller
              name="nodeData.payload.due_on_value"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  size="md"
                  label={i18n.t('workflows.sidepanel.task.due_on_label')}
                  hideLabel
                  placeholder="2"
                  value={String(field.value)}
                  onChange={(value) => field.onChange(Number(value))}
                  error={fieldState.error?.message}
                  type="number"
                  disabled={readonly}
                />
              )}
            />
          </Box>
          <Box width="full">
            <Controller
              name="nodeData.payload.due_on_unit"
              control={control}
              render={({ field, fieldState }) => (
                <F0Select
                  size="md"
                  label={i18n.t('workflows.sidepanel.task.due_on_label')}
                  hideLabel
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  options={getDurationUnitOptions()}
                  disabled={readonly}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
